import React, { useState, useEffect, useCallback, useMemo } from "react";
import useEmblaCarousel from "embla-carousel-react";
import ClassNames from "embla-carousel-class-names";
import Autoplay from "embla-carousel-autoplay";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { type CarouselApi } from "@/components/ui/carousel";
import { cn } from "@/lib/utils";
import CarouselCard from "./CarouselCard";
import CarouselNavigation from "./CarouselNavigation";

import "./HomepageCarousel.css";

type HomepageCarouselProps = {
  classname?: string;
  slides: SlideProps[];
  onCtaClick: (modalId: string) => void;
  isPlaying: boolean;
  setIsPlaying: (isPlaying: (prevIsPlaying: boolean) => boolean) => void;
  slideHeight: string;
};

export type SlideProps = {
  id: string;
  title: string;
  description: string;
  image: string;
  cta: {
    label: string;
    href: string;
  };
};

const HomepageCarousel: React.FC<HomepageCarouselProps> = ({
  classname,
  slides,
  onCtaClick,
  isPlaying,
  setIsPlaying,
  slideHeight,
}) => {
  const autoplay = Autoplay({ delay: 5000 });
  const [emblaRef] = useEmblaCarousel({ loop: true }, [ClassNames(), autoplay]);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [api, setApi] = useState<CarouselApi | null>(null);

  const onSlidesChanged = useCallback(() => {
    if (api) {
      setScrollSnaps(api.scrollSnapList());
    }
  }, [api]);

  const onSelect = useCallback(() => {
    if (api) {
      setSelectedIndex(api.selectedScrollSnap());
    }
  }, [api]);

  useEffect(() => {
    if (!api) return;

    api.on("slidesChanged", onSlidesChanged);
    api.on("select", onSelect);

    return () => {
      api.off("slidesChanged", onSlidesChanged);
      api.off("select", onSelect);
    };
  }, [api, onSlidesChanged, onSelect]);

  useEffect(() => {
    if (api) {
      if (isPlaying) {
        api.plugins().autoplay.play();
      } else {
        api.plugins().autoplay.stop();
      }
    }
  }, [api, isPlaying]);

  const togglePlayPause = useCallback(() => {
    setIsPlaying((prevIsPlaying) => !prevIsPlaying);
    autoplay.reset();
  }, [setIsPlaying, autoplay]);

  const handleUserInteraction = useCallback(() => {
    autoplay.reset();
  }, [autoplay]);

  const carouselItems = useMemo(
    () =>
      slides.map((slide, index) => (
        <CarouselItem
          key={slide.id}
          className={cn("embla__slide border-transparent", {
            "is-active": index === selectedIndex,
          })}
          style={{ height: slideHeight }}
        >
          <CarouselCard
            slide={slide}
            onCtaClick={onCtaClick}
            isActive={index === selectedIndex}
          />
        </CarouselItem>
      )),
    [slides, selectedIndex, onCtaClick, slideHeight],
  );

  return (
    <div
      className={cn(
        classname,
        "relative mt-24 w-full overflow-hidden bg-transparent pt-16",
      )}
    >
      <Carousel
        setApi={setApi}
        plugins={[autoplay]}
        opts={{ align: "center", loop: true, slidesToScroll: 1 }}
        className="embla w-full bg-transparent"
        ref={emblaRef}
      >
        <CarouselContent className="embla__container bg-transparent">
          {carouselItems}
        </CarouselContent>
        <CarouselNavigation
          api={api}
          scrollSnaps={scrollSnaps}
          selectedIndex={selectedIndex}
          isPlaying={isPlaying}
          togglePlayPause={togglePlayPause}
          onUserInteraction={handleUserInteraction}
        />
      </Carousel>
    </div>
  );
};

export default HomepageCarousel;
